import { ChevronDown } from 'lucide-react';
import * as React from 'react';

import { cn } from '@heidi/utils';

import { Button } from '../button';
import { useIsExperimentalDesignSystemForms } from '../hooks/use-is-experimental-design-system-forms';
import { typographyVariant } from '../typography';

import {
  comboboxTriggerBoxClassesExperimental,
  comboboxTriggerErrorClassesExperimental,
  comboboxTriggerIconClassesExperimental,
  comboboxTriggerPlaceholderClassesExperimental,
  comboboxTriggerTextClassesExperimental,
} from './combobox.experimental.styles';
import {
  comboboxLeadingIconClasses,
  comboboxTriggerErrorClasses,
  comboboxTriggerIconClasses,
  comboboxTriggerPlaceholderClasses,
  comboboxTriggerVariants,
} from './combobox.styles';
import type { ComboboxTriggerProps } from './combobox.types';

/**
 * The control-tier trigger shared by single- and multi-select Combobox, extracted
 * so both modes render one trigger and non-combobox field shells can reuse it. Sits
 * inside `<PopoverTrigger asChild>`; the label is passed as `children`, and
 * `isPlaceholder` tints it — callers pass the flag, never the colour.
 */
const ComboboxTrigger = React.forwardRef<HTMLButtonElement, ComboboxTriggerProps>(
  ({ open, error, isPlaceholder, leadingIcon, className, children, ...rest }, ref) => {
    const isExperimentalForms = useIsExperimentalDesignSystemForms();
    const labelSlot = (
      <span
        className={cn(
          'min-w-0 flex-1 text-start',
          isExperimentalForms
            ? comboboxTriggerTextClassesExperimental
            : typographyVariant({ variant: 'p2' }),
          isExperimentalForms &&
            isPlaceholder &&
            comboboxTriggerPlaceholderClassesExperimental,
          isExperimentalForms &&
            error &&
            isPlaceholder &&
            comboboxTriggerErrorClassesExperimental,
          // block gives `text-overflow: ellipsis` a constrained box to paint in;
          // min-w-0 lets it shrink instead of shoving the chevron out.
          'block truncate'
        )}
      >
        {children}
      </span>
    );

    return (
      <Button
        ref={ref}
        // Spread first so Radix's Slot-injected handlers/`data-state` flow onto
        // the Button, while the trigger's own identity attributes below still
        // win (the injected `aria-haspopup="dialog"` must not beat `listbox`).
        {...rest}
        type="button"
        // Flag-conditional: stable `md` is also `h-9`, so an unconditional flip
        // would shrink the flag-off trigger from its 40px `lg` too.
        size={isExperimentalForms ? 'md' : 'lg'}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={error || undefined}
        variant={error ? 'destructive_outline_with_border' : 'outline'}
        // Combobox sizes its own icons; opt out of the Button's automatic SVG
        // sizing so the experimental Button's higher-specificity selector can't
        // override the leading icon's size-4.
        disableAutomaticSvgSizing
        disableAutomaticTypographyClasses={isExperimentalForms || undefined}
        className={cn(
          comboboxTriggerVariants(),
          // After `comboboxTriggerVariants()` so tailwind-merge resolves its
          // `disabled:bg-surface-disabled` onto the control tier too.
          isExperimentalForms && comboboxTriggerBoxClassesExperimental,
          // The label also restates experimental placeholder/error colour because
          // its value text class would otherwise win over inheritance.
          isPlaceholder &&
            (isExperimentalForms
              ? comboboxTriggerPlaceholderClassesExperimental
              : comboboxTriggerPlaceholderClasses),
          error &&
            (isExperimentalForms
              ? comboboxTriggerErrorClassesExperimental
              : comboboxTriggerErrorClasses),
          className
        )}
      >
        {leadingIcon ? (
          <span className="flex min-w-0 flex-1 items-center gap-2 text-start">
            <span className={comboboxLeadingIconClasses}>{leadingIcon}</span>
            {labelSlot}
          </span>
        ) : (
          labelSlot
        )}
        <ChevronDown
          className={cn(
            comboboxTriggerIconClasses,
            isExperimentalForms && comboboxTriggerIconClassesExperimental
          )}
        />
      </Button>
    );
  }
);
ComboboxTrigger.displayName = 'ComboboxTrigger';

export { ComboboxTrigger };
